import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/browser';
import { Settings } from 'luxon';
import { RemoteConfigService } from 'src/app/lib/remote-config/remote-config.service';
import { AgreementStatusesRepository } from 'src/app/models/agreement-statuses/agreement-statuses-repository';
import { AvailableAreasRepository } from 'src/app/models/available-areas/available-areas-repository';
import { PrivacyPolicyRepository } from 'src/app/models/privacy-policy/privacy-policy-repository';
import { SearchAreaGroupRepository } from 'src/app/models/search-area-group/search-area-group-repository';
import { SearchAreaRepository } from 'src/app/models/search-area/search-area-repository';
import { TermsOfServiceRepository } from 'src/app/models/terms-of-service/terms-of-service-repository';
import { UserProfileRepository } from 'src/app/models/user-profile/user-profile-repository';
import { AnalyticsService } from '../../lib/analytics/analytics.service';
import { AuthService } from '../../lib/auth.service';
import { AgreementStatuses } from '../../models/agreement-statuses/agreement-statuses';
import {
  storeAgreementStatuses,
  storePrivacyPolicy,
  storeTermsOfService,
} from './agreement-statuses/agreement-statuses.actions';
import { Dispatcher } from './dispatcher';
import { makeRestoreSearchStateParams } from './search/make-restore-seatch-state-params';
import {
  restoreSearchState,
  storeAvailableAreas,
  storeSearchAreaGroups,
  storeSearchAreas,
} from './search/search.actions';
import { storeUserProfile } from './user-profile/user-profile.actions';

/**
 * Initializer は SharedModule にて明示的に provide する。
 */
@Injectable()
export class Initializer {
  constructor(
    private auth: AuthService,
    private profileRepository: UserProfileRepository,
    private searchAreaGroupRepository: SearchAreaGroupRepository,
    private searchAreaRepository: SearchAreaRepository,
    private availableAreasRepository: AvailableAreasRepository,
    private dispatcher: Dispatcher,
    private agreementStatusesRepository: AgreementStatusesRepository,
    private privacyPolicyRepository: PrivacyPolicyRepository,
    private termsOfServiceRepository: TermsOfServiceRepository,
    private remoteConfig: RemoteConfigService,
    private analyticsService: AnalyticsService,
  ) {}

  async init(): Promise<void> {
    // 現状、国外に提供する予定は無いので、timezone は Asia/Tokyo に固定する
    Settings.defaultZoneName = 'Asia/Tokyo';

    try {
      const [currentUser, availableAreas, searchAreaGroups] = await Promise.all(
        [
          this.auth.verify(),
          this.availableAreasRepository.getAll(),
          this.searchAreaGroupRepository.getAll(),
          this.remoteConfig.fetchAndActivate(),
        ],
      );

      if (currentUser !== null) {
        // firebase のイベントに userId を付与する。
        this.analyticsService.setUserId(currentUser.uid);

        // FireStore から UserProfile と AgreementStatuses を取得する。
        try {
          const [profile, agreementStatuses] = await Promise.all([
            this.profileRepository.get(currentUser.uid),
            this.agreementStatusesRepository.get(currentUser.uid),
          ]);

          if (profile !== null) {
            this.dispatcher.dispatch(storeUserProfile(profile));
          }

          if (agreementStatuses !== null) {
            this.dispatcher.dispatch(storeAgreementStatuses(agreementStatuses));
          } else {
            this.dispatcher.dispatch(
              storeAgreementStatuses(new AgreementStatuses()),
            );
          }
        } catch (e) {
          Sentry.captureException(e);
          // @TODO エラー画面に遷移する
        }
      }

      // FireStore から取得した検索可能地域を保存する。
      if (availableAreas !== null) {
        this.dispatcher.dispatch(storeAvailableAreas(availableAreas));
      }

      // FireStore から取得した検索地域リストを保存する。
      if (searchAreaGroups !== null) {
        this.dispatcher.dispatch(storeSearchAreaGroups(searchAreaGroups));
        // 取得したエリアグループに紐づくエリアをすべて取得して保存する。
        for (const searchAreaGroup of searchAreaGroups) {
          const searchAreas = await this.searchAreaRepository.getAllBySearchAreaGroup(
            searchAreaGroup.id,
          );
          this.dispatcher.dispatch(
            storeSearchAreas({
              searchAreaGroupKey: searchAreaGroup.key,
              searchAreas,
            }),
          );
        }

        /**
         * search state を URL から復元する。
         */
        const params = new URLSearchParams(window.location.search);
        this.dispatcher.dispatch(
          restoreSearchState(
            makeRestoreSearchStateParams({
              seats: params.get('seats'),
              lat: params.get('lat'),
              lng: params.get('lng'),
              view: params.get('view'),
              area: params.get('area'),
              areaGroup: params.get('areaGroup'),
              startsFromTime: params.get('startsFromTime'),
            }),
          ),
        );
      }

      // Remote Config から Privacy policy version を取得する
      const privacyPolicy = this.privacyPolicyRepository.get();
      this.dispatcher.dispatch(storePrivacyPolicy(privacyPolicy));

      // Remote Config から Terms of service version を取得する
      const termsOfService = this.termsOfServiceRepository.get();
      this.dispatcher.dispatch(storeTermsOfService(termsOfService));
    } catch (e) {
      Sentry.captureException(e);
      // @TODO エラー画面に遷移する
    }
  }
}
